import { FunctionComponent } from 'react';
import { EmailValidationSVG, EmailValidationCongratsSVG } from '../../../../components/svg/hub/email-validation';
import { PhoneValidationSVG, PhoneValidationCongratsSVG } from '../../../../components/svg/hub/phone-validation';
import { KycSVG, KycCongratsSVG, KycConflictSVG } from '../../../../components/svg/hub/kyc';
import pixSVG from '../../../../components/svg/hub/pix';
import { PasswordSVG, PasswordCongratsSVG } from '../../../../components/svg/hub/password';
import { RegulationsSVG, RegulationsCongratsSVG } from '../../../../components/svg/hub/regulations';
import { TycSVG, TycCongratsSVG } from '../../../../components/svg/hub/tyc';
import { KycCompanyValidationSVG, KycCompanyValidationCongratsSVG, KycCompanyValidationConflictSVG } from '../../../../components/svg/hub/kyc_company';
import { KycLightSVG, KycLightCongratsSVG } from '../../../../components/svg/hub/kyc_light';

import { EmailData, KycData, PhoneData, PixData, RegulationsData, TycData, KycCompanyData, KycOnTheFlyData } from '../../../../../services/registration-hub-api/interfaces';

import type { Step } from '../../../../../services/types';

type StepMapperDescription = (description?: string, data?: any) => string | undefined;
type StepMapperContent = {
  asset: FunctionComponent,
  congratsAsset: FunctionComponent,
  conflictAsset?: FunctionComponent,
  description: StepMapperDescription
};

const StepMapper: Record<Step, StepMapperContent> = {
  email_validation: {
    asset: EmailValidationSVG,
    congratsAsset: EmailValidationCongratsSVG,
    description: (
      description?: string,
      data?: EmailData,
    ): string => description || data!.email,
  },
  declared_email: {
    asset: EmailValidationSVG,
    congratsAsset: EmailValidationCongratsSVG,
    description: (
      description?: string,
      data?: EmailData,
    ): string => description || data!.email,
  },
  phone_validation: {
    asset: PhoneValidationSVG,
    congratsAsset: PhoneValidationCongratsSVG,
    description: (
      description?: string,
      data?: PhoneData,
    ): string => description || data!.phone,
  },
  kyc: {
    asset: KycSVG,
    congratsAsset: KycCongratsSVG,
    conflictAsset: KycConflictSVG,
    description: (
      description?: string,
      data?: KycData,
    ): string => description || data!.identity,
  },
  kyc_paes: {
    asset: KycCompanyValidationSVG,
    congratsAsset: KycCompanyValidationCongratsSVG,
    conflictAsset: KycCompanyValidationConflictSVG,
    description: (
      description?: string,
      data?: KycData,
    ): string => description || data!.identity,
  },
  pix: {
    asset: pixSVG,
    congratsAsset: EmailValidationCongratsSVG,
    description: (
      description?: string,
      data?: PixData,
    ): string => description || data!.info,
  },
  regulations: {
    asset: RegulationsSVG,
    congratsAsset: RegulationsCongratsSVG,
    description: (
      description?: string,
      data?: RegulationsData,
    ): string | undefined => description || data?.regulations,
  },
  password: {
    asset: PasswordSVG,
    congratsAsset: PasswordCongratsSVG,
    description: (description?: string): string | undefined => description,
  },
  tyc: {
    asset: TycSVG,
    congratsAsset: TycCongratsSVG,
    description: (description?: string): string | undefined => description,
  },
  kyc_company: {
    asset: KycCompanyValidationSVG,
    congratsAsset: KycCompanyValidationCongratsSVG,
    conflictAsset: KycCompanyValidationConflictSVG,
    description: (
      description?: string,
      data?: KycCompanyData,
    ): string => description || data!.company,
  },
  checked_email_validation: {
    asset: EmailValidationSVG,
    congratsAsset: EmailValidationCongratsSVG,
    description: (
      description?: string,
      data?: EmailData,
    ): string => description || data!.email,
  },
  kyc_light: {
    asset: KycLightSVG,
    congratsAsset: KycLightCongratsSVG,
    conflictAsset: KycConflictSVG,
    description: (
      description?: string,
      data?: KycData,
    ): string => description || data!.identity,
  },
  checked_invitation_email_validation: {
    asset: EmailValidationSVG,
    congratsAsset: EmailValidationCongratsSVG,
    description: (
      description?: string,
      data?: EmailData,
    ): string => description || data!.email,
  },
  kyc_unified: {
    asset: KycLightSVG,
    congratsAsset: KycLightCongratsSVG,
    conflictAsset: KycConflictSVG,
    description: (
      description?: string,
      data?: KycData,
    ): string => description || data!.identity,
  },
  kyc_on_the_fly: {
    asset: KycCompanyValidationSVG,
    congratsAsset: KycCompanyValidationCongratsSVG,
    conflictAsset: KycCompanyValidationConflictSVG,
    description: (
      description?: string,
      data?: KycOnTheFlyData,
    ): string => description || data!.company || data!.identity,
  },
  kyc_business_data: {
    asset: KycCompanyValidationSVG,
    congratsAsset: KycCompanyValidationCongratsSVG,
    conflictAsset: KycCompanyValidationConflictSVG,
    description: (
      description?: string,
      data?: KycCompanyData,
    ): string => description || data!.company || data!.identity,
  },
  kyc_legal_representative: {
    asset: KycSVG,
    congratsAsset: KycCongratsSVG,
    conflictAsset: KycConflictSVG,
    description: (
      description?: string,
      data?: KycData,
    ): string => description || data!.identity,
  },
  kyc_with_session: {
    asset: KycSVG,
    congratsAsset: KycCongratsSVG,
    conflictAsset: KycConflictSVG,
    description: (
      description?: string,
      data?: KycData,
    ): string | undefined => description || data!.identity,
  },
  tyc_with_session: {
    asset: TycSVG,
    congratsAsset: TycCongratsSVG,
    description: (description?: string): string | undefined => description,
  },
  phone_validation_with_session: {
    asset: PhoneValidationSVG,
    congratsAsset: PhoneValidationCongratsSVG,
    description: (
      description?: string,
      data?: PhoneData,
    ): string => description || data!.phone,
  },
};

export default StepMapper;
